import React, { useEffect, useState, useRef, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { createPortal } from "react-dom";
import {
  FaEye,
  FaPrint,
  FaFileExcel,
  FaFilePdf,
  FaTimes,
} from "react-icons/fa";
import {
  getProgressBillsReport,
  clearProgressBillsReport,
  clearError,
  clearSuccess,
  getProjectsForReport,
  getContractsForReport,
} from "../../../../redux/Slices/ProjectReportsSlice";
import { useTitle } from "../../../../shared/hooks/TitleContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import * as XLSX from "xlsx";
import i18n from "../../../../i18n/i18n";
import {
  getPdfRowStyles,
  chunkArray,
  convertArabicNumbersToEnglish,
} from "../../../../shared/utils.js/utils";

const ProgressBillsReport = () => {
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { t } = useTranslation("global");
  const isRTL = i18n.language === "ar";
  const dir = isRTL ? "rtl" : "ltr";
  const headerAlign = isRTL ? "right" : "left";
  const publicUrl = process.env.PUBLIC_URL || "";
  const headerLogo = `${publicUrl}/ACE.png`;

  const {
    progressBillsReport,
    projects,
    contracts,
    isLoading,
    error,
    success,
    projectsPagination,
    contractsPagination,
  } = useSelector(state => state.projectReports);

  const getProjectName = project => {
    if (!project) return "";
    return isRTL
      ? project.name_ar || project.name_en || project.name || ""
      : project.name_en || project.name || project.name_ar || "";
  };

  const getContractName = contract => {
    if (!contract) return "";
    return contract.code || contract.name || `#${contract.id}`;
  };

  const getCustomerName = bill => {
    if (!bill) return "—";
    if (bill.customer_name) return bill.customer_name;
    const customer = bill.project?.customer || bill.customer;
    if (!customer) return "—";
    return isRTL
      ? customer.name_ar || customer.name_en || customer.name || "—"
      : customer.name_en || customer.name || customer.name_ar || "—";
  };

  const [projectId, setProjectId] = useState("");
  const [contractId, setContractId] = useState("");
  const [status, setStatus] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [minTotal, setMinTotal] = useState("");
  const [maxTotal, setMaxTotal] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState("");
  const [projectPage, setProjectPage] = useState(1);
  const projectSearchInputRef = useRef(null);

  const [contractDropdownOpen, setContractDropdownOpen] = useState(false);
  const [contractSearchTerm, setContractSearchTerm] = useState("");
  const [contractPage, setContractPage] = useState(1);
  const contractSearchInputRef = useRef(null);

  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);

  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [pdfType, setPdfType] = useState(null);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewZoom, setPreviewZoom] = useState(1.0);
  const [previewPosition, setPreviewPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [pdfChunks, setPdfChunks] = useState([]); // For paginated PDF generation
  const pageSize = 10;
  const projectsHasNext =
    projectsPagination &&
    projectsPagination.current_page < projectsPagination.last_page;
  const contractsHasNext =
    contractsPagination &&
    contractsPagination.current_page < contractsPagination.last_page;

  useEffect(() => {
    setTitle("projectReports.progressBills", {
      defaultValue: "Progress Bills Reports",
    });
    dispatch(getProjectsForReport({ page: 1, per_page: 10 }));
    dispatch(getContractsForReport({ page: 1, per_page: 10 }));
    dispatch(getProgressBillsReport({}));
    return () => {
      dispatch(clearProgressBillsReport());
    };
  }, [dispatch, setTitle]);

  useEffect(() => {
    if (success) {
      if (isInitialLoad) {
        dispatch(clearSuccess());
        return;
      }
      dispatch(clearSuccess());
    }
  }, [success, dispatch, isRTL, isInitialLoad]);

  useEffect(() => {
    if (error) {
      toast.error(error, { rtl: isRTL });
      setTimeout(() => dispatch(clearError()), 2500);
    }
  }, [error, dispatch, isRTL]);

  useEffect(() => {
    const handleMouseMove = e => {
      if (!isDragging) return;
      setPreviewPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [isDragging, dragStart]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (projectSearchTerm) {
        dispatch(
          getProjectsForReport({
            search: projectSearchTerm,
            page: 1,
            per_page: pageSize,
          }),
        );
        setProjectPage(1);
      } else {
        dispatch(
          getProjectsForReport({ page: projectPage, per_page: pageSize }),
        );
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [projectSearchTerm, dispatch, pageSize]);

  useEffect(() => {
    if (projectDropdownOpen && !projectSearchTerm) {
      dispatch(getProjectsForReport({ page: projectPage, per_page: pageSize }));
    }
  }, [projectPage, projectDropdownOpen, projectSearchTerm, dispatch, pageSize]);

  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (contractSearchTerm) {
        dispatch(
          getContractsForReport({
            search: contractSearchTerm,
            page: 1,
            project_id: projectId || undefined,
            per_page: pageSize,
          }),
        );
        setContractPage(1);
      } else {
        dispatch(
          getContractsForReport({
            page: contractPage,
            project_id: projectId || undefined,
            per_page: pageSize,
          }),
        );
      }
    }, 300);

    return () => clearTimeout(debounceTimer);
  }, [contractSearchTerm, dispatch, projectId, pageSize]);

  useEffect(() => {
    if (contractDropdownOpen && !contractSearchTerm) {
      dispatch(
        getContractsForReport({
          page: contractPage,
          project_id: projectId || undefined,
          per_page: pageSize,
        }),
      );
    }
  }, [
    contractPage,
    contractDropdownOpen,
    contractSearchTerm,
    dispatch,
    projectId,
    pageSize,
  ]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (
        projectDropdownOpen &&
        !e.target.closest(".project-dropdown-container")
      ) {
        setProjectDropdownOpen(false);
      }
      if (
        contractDropdownOpen &&
        !e.target.closest(".contract-dropdown-container")
      ) {
        setContractDropdownOpen(false);
      }
      if (
        statusDropdownOpen &&
        !e.target.closest(".status-dropdown-container")
      ) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [projectDropdownOpen, contractDropdownOpen, statusDropdownOpen]);

  const filteredProjects = useMemo(() => {
    if (!Array.isArray(projects)) return [];
    if (!projectSearchTerm) return projects;
    const term = projectSearchTerm.toLowerCase();
    return projects.filter(project => {
      const nameAr = project.name_ar?.toLowerCase() || "";
      const name = project.name?.toLowerCase() || "";
      const nameEn = project.name_en?.toLowerCase() || "";
      const code = project.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) ||
        name.includes(term) ||
        nameEn.includes(term) ||
        code.includes(term)
      );
    });
  }, [projects, projectSearchTerm]);

  const filteredContracts = useMemo(() => {
    if (!Array.isArray(contracts)) return [];
    if (!contractSearchTerm) return contracts;
    const term = contractSearchTerm.toLowerCase();
    return contracts.filter(contract => {
      const nameAr = contract.name_ar?.toLowerCase() || "";
      const name = contract.name?.toLowerCase() || "";
      const nameEn = contract.name_en?.toLowerCase() || "";
      const code = contract.code?.toLowerCase() || "";
      return (
        nameAr.includes(term) ||
        name.includes(term) ||
        nameEn.includes(term) ||
        code.includes(term)
      );
    });
  }, [contracts, contractSearchTerm]);

  const handleSubmit = e => {
    e.preventDefault();
    setIsInitialLoad(false);
    const params = {};
    if (projectId) params.project_id = projectId;
    if (contractId) params.contract_id = contractId;
    if (status) params.status = status;
    if (fromDate) params.from_date = fromDate;
    if (toDate) params.to_date = toDate;
    if (minTotal) params.min_total = minTotal;
    if (maxTotal) params.max_total = maxTotal;
    if (sortBy) params.sort_by = sortBy;
    if (sortOrder) params.sort_order = sortOrder;
    dispatch(getProgressBillsReport(params));
  };

  const handlePreviewAll = async () => {
    if (!progressBillsReport) {
      toast.error(
        t("projectReports.noData", { defaultValue: "No data to preview" }),
        { rtl: isRTL },
      );
      return;
    }
    try {
      const reportData = progressBillsReport?.data || progressBillsReport || {};
      const progressBills = reportData.data || reportData.progress_bills || [];
      const total = progressBills.length || 0;

      if (total === 0) {
        toast.error(
          t("projectReports.noData", { defaultValue: "No data to preview" }),
          { rtl: isRTL },
        );
        return;
      }

      const response = await dispatch(
        getProgressBillsReport({
          page: 1,
          per_page: total,
          project_id: projectId,
          contract_id: contractId,
          status: status,
          from_date: fromDate,
          to_date: toDate,
          min_total: minTotal,
          max_total: maxTotal,
          sort_by: sortBy,
          sort_order: sortOrder,
        }),
      ).unwrap();

      const allBills =
        response?.data?.data ||
        response?.data?.progress_bills ||
        response?.data ||
        [];
      const chunks = chunkArray(allBills, 20);
      setPdfChunks(chunks);
      setIsPreviewOpen(true);
      setPreviewZoom(1.0);
      setPreviewPosition({ x: 0, y: 0 });
    } catch (error) {
      console.error("Failed to fetch all progress bills for preview:", error);
      toast.error(
        t("projectReports.fetchError", {
          defaultValue: "Failed to fetch all progress bills for preview",
        }),
        { rtl: isRTL },
      );
    }
  };

  const handlePrintAll = async () => {
    if (!progressBillsReport) {
      toast.error(
        t("projectReports.noData", { defaultValue: "No data to print" }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      setIsGeneratingPdf(true);

      const reportData = progressBillsReport?.data || progressBillsReport || {};
      const currentBills = reportData.data || reportData.progress_bills || [];
      const total =
        reportData.meta?.total ||
        reportData.pagination?.total ||
        currentBills.length ||
        0;

      if (total === 0) {
        toast.error(
          t("projectReports.noData", { defaultValue: "No data to print" }),
          { rtl: isRTL },
        );
        setIsGeneratingPdf(false);
        return;
      }

      const response = await dispatch(
        getProgressBillsReport({
          page: 1,
          per_page: total,
          project_id: projectId,
          contract_id: contractId,
          status: status,
          from_date: fromDate,
          to_date: toDate,
          min_total: minTotal,
          max_total: maxTotal,
          sort_by: sortBy,
          sort_order: sortOrder,
        }),
      ).unwrap();

      const allBills =
        response?.data?.data ||
        response?.data?.progress_bills ||
        response?.data ||
        [];
      const chunks = chunkArray(allBills, 20);
      setPdfChunks(chunks);

      setPdfType("all");

      const waitForPdfTemplate = async (
        elementId,
        retries = 12,
        delay = 80,
      ) => {
        for (let attempt = 0; attempt < retries; attempt++) {
          const node = document.getElementById(elementId);
          if (node) return node;
          await new Promise(resolve => setTimeout(resolve, delay));
        }
        return null;
      };

      await new Promise(resolve => requestAnimationFrame(resolve));
      await new Promise(resolve => setTimeout(resolve, 120));
      if (document.fonts && document.fonts.ready) {
        await document.fonts.ready;
      }
      const preloadImage = src =>
        new Promise((resolve, reject) => {
          if (!src) return resolve();
          const img = new Image();
          img.crossOrigin = "anonymous";
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = src;
        });
      await Promise.all([
        preloadImage(`${publicUrl}/HeaderAce.png`),
        preloadImage(`${publicUrl}/FooterAce.png`),
        preloadImage(headerLogo),
      ]);
      await new Promise(resolve => setTimeout(resolve, 200));
      setIsGeneratingPdf(true);

      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const scale = Math.min(2, Math.max(1.5, devicePixelRatio));

      // Generate PDF for each chunk
      for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex++) {
        await new Promise(resolve => requestAnimationFrame(resolve));
        const element = await waitForPdfTemplate(
          `progress-bills-report-pdf-all-${chunkIndex}`,
        );
        if (!element) {
          if (chunkIndex === 0) {
            toast.error(
              t("projectReports.printError", {
                defaultValue: "Print template not available",
              }),
              { rtl: isRTL },
            );
            setIsGeneratingPdf(false);
            setPdfType(null);
            setPdfChunks([]);
            return;
          }
          continue;
        }

        const canvas = await html2canvas(element, {
          scale,
          useCORS: true,
          logging: false,
          backgroundColor: "#ffffff",
          letterRendering: false,
          allowTaint: false,
          removeContainer: false,
          imageTimeout: 10000,
          onclone: clonedDoc => {
            const clonedElement = clonedDoc.getElementById(
              `progress-bills-report-pdf-all-${chunkIndex}`,
            );
            if (clonedElement) {
              clonedElement.style.fontFamily =
                '"Times New Roman Bold", "Times New Roman", "Times", serif';
              clonedElement.style.fontWeight = "bold";
              clonedElement.style.direction = dir;
            }
          },
        });

        const imgData = canvas.toDataURL("image/png", 1.0);
        const imgHeight = (canvas.height * pdfWidth) / canvas.width;

        if (chunkIndex > 0) {
          pdf.addPage();
        }

        // Add the image to the current page. Since each chunk has a fixed height (1123px),
        // it should align perfectly with the PDF page height.
        pdf.addImage(
          imgData,
          "PNG",
          0,
          0,
          pdfWidth,
          imgHeight,
          undefined,
          "FAST",
        );
      }

      pdf.save(
        `ACE_ProgressBillsReport_${new Date().toISOString().split("T")[0]}.pdf`,
      );
      toast.success(
        t("projectReports.printed", {
          defaultValue: "Report printed successfully",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      console.error("Progress Bills Report PDF error:", error);
      toast.error(
        t("projectReports.printError", {
          defaultValue: "An error occurred while generating PDF",
        }),
        { rtl: isRTL },
      );
    } finally {
      setIsGeneratingPdf(false);
      setPdfType(null);
      setPdfChunks([]);
    }
  };

  const handleExportAllToExcel = () => {
    if (!progressBillsReport) {
      toast.error(
        t("projectReports.noData", { defaultValue: "No data to export" }),
        { rtl: isRTL },
      );
      return;
    }

    try {
      const data = progressBillsReport?.data || progressBillsReport || {};
      const bills = data.data || data.progress_bills || [];

      const excelData = [
        {
          [t("projectReports.period", { defaultValue: "Period" })]:
            `${formatDate(fromDate)} - ${formatDate(toDate)}`,
        },
        {},
        ...bills.map(bill => ({
          [t("projectReports.billNo", { defaultValue: "Bill No." })]:
            bill.bill_no || "—",
          [t("projectReports.date", { defaultValue: "Date" })]: formatDate(
            bill.date,
          ),
          [t("projectReports.project", { defaultValue: "Project" })]: isRTL
            ? bill.project?.name_ar ||
              bill.project?.name_en ||
              bill.project?.name
            : bill.project?.name_en ||
              bill.project?.name ||
              bill.project?.name_ar,
          [t("projectReports.customer", { defaultValue: "Customer" })]:
            getCustomerName(bill),
          [t("projectReports.contract", { defaultValue: "Contract" })]:
            bill.contract?.code || "—",
          [t("projectReports.status", { defaultValue: "Status" })]:
            translateStatus(bill.status),
          [t("projectReports.total", { defaultValue: "Total" })]: formatNumber(
            bill.current_bill_amount || 0,
          ),
        })),
      ];

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        "Progress Bills Report",
      );
      XLSX.writeFile(
        workbook,
        `ProgressBillsReport_${new Date().toISOString().split("T")[0]}.xlsx`,
      );
      toast.success(
        t("projectReports.exported", {
          defaultValue: "Data exported successfully",
        }),
        { rtl: isRTL },
      );
    } catch (error) {
      toast.error(
        error.message ||
          t("projectReports.exportError", {
            defaultValue: "Failed to export data",
          }),
        { rtl: isRTL },
      );
    }
  };

  const formatNumber = value =>
    convertArabicNumbersToEnglish(
      Number(value ?? 0).toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );

  const formatDate = dateString => {
    if (!dateString) return "—";
    return convertArabicNumbersToEnglish(
      new Date(dateString).toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }),
    );
  };

  const translateStatus = value => {
    if (!value) return "—";
    if (value === "draft")
      return t("projectReports.draft", { defaultValue: "مسودة" });
    if (value === "approved")
      return t("projectReports.approved", { defaultValue: "موافق عليه" });
    if (value === "pending")
      return t("projectReports.pending", { defaultValue: "في الانتظار" });
    if (value === "rejected")
      return t("projectReports.rejected", { defaultValue: "مرفوض" });
    return value;
  };

  const reportData = progressBillsReport?.data || progressBillsReport || {};
  const progressBills = reportData.data || reportData.progress_bills || [];
  const summary = reportData.summary || {};
  const filtersApplied = reportData.filters_applied || {};
  const selectedProject = projects.find(
    p => String(p.id) === String(projectId),
  );
  const selectedContract = contracts.find(
    c => String(c.id) === String(contractId),
  );

  return (
    <div style={{ padding: "20px", direction: dir }}>
      <div
        style={{
          backgroundColor: "#1b3350",
          borderRadius: "12px",
          padding: "24px",
          border: "1px solid #333",
        }}
      >
        <h2
          style={{
            color: "white",
            marginBottom: "20px",
            textAlign: headerAlign,
          }}
        >
          {t("projectReports.progressBills", {
            defaultValue: "Progress Bills Reports",
          })}
        </h2>

        <form
          onSubmit={handleSubmit}
          style={{
            backgroundColor: "#1a1f2e",
            padding: "20px",
            borderRadius: "8px",
            marginBottom: "20px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
              gap: "15px",
              marginBottom: "15px",
            }}
          >
            <div
              style={{ position: "relative" }}
              className="project-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.project", { defaultValue: "Project" })}
              </label>
              <div
                onClick={() => {
                  setProjectDropdownOpen(!projectDropdownOpen);
                  if (!projectDropdownOpen) {
                    setTimeout(() => {
                      if (projectSearchInputRef.current) {
                        projectSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {selectedProject
                    ? getProjectName(selectedProject)
                    : t("projectReports.selectProject", {
                        defaultValue: "Select project",
                      })}
                </span>
                <span>▼</span>
              </div>
              {projectDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={projectSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={projectSearchTerm}
                      onChange={e => setProjectSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #333",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setProjectPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={projectPage === 1}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: projectPage === 1 ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: projectPage === 1 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        color: "#ccc",
                        fontSize: "12px",
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      {t("projectReports.page", { defaultValue: "Page" })}{" "}
                      {projectPage}{" "}
                      {projectsPagination?.last_page
                        ? ` / ${projectsPagination.last_page}`
                        : ""}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setProjectPage(prev => prev + 1);
                      }}
                      disabled={!projectsHasNext}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: !projectsHasNext ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: !projectsHasNext ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {filteredProjects.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#ccc",
                      }}
                    >
                      {t("projectReports.noData", { defaultValue: "No data" })}
                    </div>
                  ) : (
                    <>
                      {filteredProjects.map(project => (
                        <div
                          key={project.id}
                          onClick={() => {
                            setProjectId(String(project.id));
                            setProjectDropdownOpen(false);
                            setProjectSearchTerm("");
                            setContractId("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            borderBottom: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(projectId) === String(project.id)
                                ? "#2a2a2a"
                                : "transparent",
                            textAlign: headerAlign,
                            direction: dir,
                          }}
                        >
                          {project.code ? `${project.code} - ` : ""}
                          {getProjectName(project)}
                        </div>
                      ))}
                      <div style={{ padding: "10px" }}></div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div
              style={{ position: "relative" }}
              className="contract-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.contract", { defaultValue: "Contract" })}
              </label>
              <div
                onClick={() => {
                  setContractDropdownOpen(!contractDropdownOpen);
                  if (!contractDropdownOpen) {
                    setTimeout(() => {
                      if (contractSearchInputRef.current) {
                        contractSearchInputRef.current.focus();
                      }
                    }, 100);
                  }
                }}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {selectedContract
                    ? getContractName(selectedContract)
                    : t("projectReports.selectContract", {
                        defaultValue: "Select contract",
                      })}
                </span>
                <span>▼</span>
              </div>
              {contractDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "400px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  <div
                    style={{ padding: "8px", borderBottom: "1px solid #333" }}
                  >
                    <input
                      ref={contractSearchInputRef}
                      type="text"
                      placeholder={t("common.search", {
                        defaultValue: isRTL ? "بحث..." : "Search...",
                      })}
                      value={contractSearchTerm}
                      onChange={e => setContractSearchTerm(e.target.value)}
                      onClick={e => e.stopPropagation()}
                      style={{
                        width: "100%",
                        padding: "8px",
                        backgroundColor: "#0d1117",
                        border: "none",
                        borderBottom: "1px solid #333",
                        color: "white",
                        borderRadius: "6px 6px 0 0",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    />
                  </div>
                  <div
                    style={{
                      padding: "8px",
                      borderBottom: "1px solid #333",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "10px",
                    }}
                  >
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setContractPage(prev => Math.max(1, prev - 1));
                      }}
                      disabled={contractPage === 1}
                      style={{
                        padding: "5px 10px",
                        backgroundColor:
                          contractPage === 1 ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: contractPage === 1 ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ‹
                    </button>
                    <span
                      style={{
                        color: "#ccc",
                        fontSize: "12px",
                        flex: 1,
                        textAlign: "center",
                      }}
                    >
                      {t("projectReports.page", { defaultValue: "Page" })}{" "}
                      {contractPage}{" "}
                      {contractsPagination?.last_page
                        ? ` / ${contractsPagination.last_page}`
                        : ""}
                    </span>
                    <button
                      onClick={e => {
                        e.stopPropagation();
                        setContractPage(prev => prev + 1);
                      }}
                      disabled={!contractsHasNext}
                      style={{
                        padding: "5px 10px",
                        backgroundColor: !contractsHasNext ? "#666" : "#AC2000",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: !contractsHasNext ? "not-allowed" : "pointer",
                        fontSize: "12px",
                      }}
                    >
                      ›
                    </button>
                  </div>
                  {filteredContracts.length === 0 ? (
                    <div
                      style={{
                        padding: "20px",
                        textAlign: "center",
                        color: "#ccc",
                      }}
                    >
                      {t("projectReports.noData", { defaultValue: "No data" })}
                    </div>
                  ) : (
                    <>
                      {filteredContracts.map(contract => (
                        <div
                          key={contract.id}
                          onClick={() => {
                            setContractId(String(contract.id));
                            setContractDropdownOpen(false);
                            setContractSearchTerm("");
                          }}
                          style={{
                            padding: "10px",
                            cursor: "pointer",
                            borderBottom: "1px solid #333",
                            color: "white",
                            backgroundColor:
                              String(contractId) === String(contract.id)
                                ? "#2a2a2a"
                                : "transparent",
                            textAlign: headerAlign,
                            direction: dir,
                          }}
                        >
                          {contract.code ? `${contract.code} - ` : ""}
                          {getContractName(contract)}
                        </div>
                      ))}
                      <div style={{ padding: "10px" }}></div>
                    </>
                  )}
                </div>
              )}
            </div>

            <div
              style={{ position: "relative" }}
              className="status-dropdown-container"
            >
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.status", { defaultValue: "Status" })}
              </label>
              <div
                onClick={() => setStatusDropdownOpen(!statusDropdownOpen)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                  cursor: "pointer",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  textAlign: headerAlign,
                  direction: dir,
                }}
              >
                <span>
                  {status
                    ? translateStatus(status)
                    : t("projectReports.selectStatus", {
                        defaultValue: "Select status",
                      })}
                </span>
                <span>▼</span>
              </div>
              {statusDropdownOpen && (
                <div
                  style={{
                    position: "absolute",
                    top: "100%",
                    left: 0,
                    right: 0,
                    backgroundColor: "#14263b",
                    border: "1px solid #333",
                    borderRadius: "6px",
                    marginTop: "4px",
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 1000,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
                  }}
                >
                  {[
                    {
                      value: "approved",
                      label: t("projectReports.approved", {
                        defaultValue: "Approved",
                      }),
                    },
                    {
                      value: "pending",
                      label: t("projectReports.pending", {
                        defaultValue: "Pending",
                      }),
                    },
                    {
                      value: "rejected",
                      label: t("projectReports.rejected", {
                        defaultValue: "Rejected",
                      }),
                    },
                  ].map(option => (
                    <div
                      key={option.value}
                      onClick={() => {
                        setStatus(option.value);
                        setStatusDropdownOpen(false);
                      }}
                      style={{
                        padding: "10px",
                        cursor: "pointer",
                        borderBottom: "1px solid #333",
                        color: "white",
                        backgroundColor:
                          status === option.value ? "#2a2a2a" : "transparent",
                        textAlign: headerAlign,
                        direction: dir,
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.fromDate", { defaultValue: "From date" })}
              </label>
              <input
                type="date"
                value={fromDate}
                onChange={e => setFromDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.toDate", { defaultValue: "To date" })}
              </label>
              <input
                type="date"
                value={toDate}
                onChange={e => setToDate(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.minTotal", {
                  defaultValue: "Minimum amount",
                })}
              </label>
              <input
                type="number"
                value={minTotal}
                onChange={e => setMinTotal(e.target.value)}
                placeholder={t("projectReports.minTotal", {
                  defaultValue: "Minimum",
                })}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.maxTotal", {
                  defaultValue: "Maximum amount",
                })}
              </label>
              <input
                type="number"
                value={maxTotal}
                onChange={e => setMaxTotal(e.target.value)}
                placeholder={t("projectReports.maxTotal", {
                  defaultValue: "Maximum",
                })}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              />
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.sortBy", { defaultValue: "Sort by" })}
              </label>
              <select
                value={sortBy}
                onChange={e => setSortBy(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              >
                <option value="created_at">
                  {t("projectReports.createdAt", {
                    defaultValue: "Created at",
                  })}
                </option>
                <option value="date">
                  {t("projectReports.date", { defaultValue: "Date" })}
                </option>
                <option value="total">
                  {t("projectReports.total", { defaultValue: "Total" })}
                </option>
              </select>
            </div>

            <div>
              <label
                style={{
                  color: "#ccc",
                  fontSize: "14px",
                  display: "block",
                  marginBottom: "8px",
                  textAlign: headerAlign,
                }}
              >
                {t("projectReports.sortOrder", { defaultValue: "Order" })}
              </label>
              <select
                value={sortOrder}
                onChange={e => setSortOrder(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  backgroundColor: "#14263b",
                  border: "1px solid #333",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "16px",
                }}
              >
                <option value="asc">
                  {t("projectReports.asc", { defaultValue: "Ascending" })}
                </option>
                <option value="desc">
                  {t("projectReports.desc", { defaultValue: "Descending" })}
                </option>
              </select>
            </div>
          </div>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: "15px",
            }}
          >
            <button
              type="submit"
              disabled={isLoading}
              style={{
                backgroundColor: isLoading ? "#666" : "#AC2000",
                color: "white",
                border: "none",
                padding: "12px 24px",
                borderRadius: "8px",
                cursor: isLoading ? "not-allowed" : "pointer",
                fontSize: "16px",
                opacity: isLoading ? 0.6 : 1,
              }}
            >
              {isLoading
                ? t("projectReports.loading", {
                    defaultValue: "جاري التحميل...",
                  })
                : t("projectReports.viewReport", {
                    defaultValue: "عرض التقرير",
                  })}
            </button>
          </div>
        </form>

        {progressBillsReport && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              gap: "10px",
              marginBottom: "20px",
              flexWrap: "wrap",
            }}
          >
            <button
              onClick={handlePreviewAll}
              style={{
                backgroundColor: "#444",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaEye />{" "}
              {t("projectReports.previewAllPDF", {
                defaultValue: "Preview all PDF",
              })}
            </button>
            <button
              onClick={handlePrintAll}
              disabled={isGeneratingPdf}
              style={{
                backgroundColor: isGeneratingPdf ? "#666" : "#17a2b8",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
                opacity: isGeneratingPdf ? 0.6 : 1,
              }}
            >
              <FaPrint />{" "}
              {isGeneratingPdf
                ? t("projectReports.generating", {
                    defaultValue: "Generating...",
                  })
                : t("projectReports.printAllPDF", {
                    defaultValue: "Print all PDF",
                  })}
            </button>
            <button
              onClick={handleExportAllToExcel}
              style={{
                backgroundColor: "#28a745",
                color: "white",
                border: "none",
                padding: "12px 20px",
                borderRadius: 8,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 8,
                justifyContent: "center",
              }}
            >
              <FaFileExcel />{" "}
              {t("projectReports.exportAllExcel", {
                defaultValue: "Download Excel (all)",
              })}
            </button>
          </div>
        )}

        {progressBillsReport && (
          <div
            style={{
              backgroundColor: "#1a1f2e",
              padding: "20px",
              borderRadius: "8px",
              marginTop: "20px",
            }}
          >
            <div
              style={{
                color: "#ccc",
                marginBottom: "20px",
                textAlign: headerAlign,
              }}
            >
              <div style={{ marginBottom: "8px" }}>
                <strong>
                  {t("projectReports.period", { defaultValue: "Period" })}:
                </strong>{" "}
                {formatDate(fromDate)} - {formatDate(toDate)}
              </div>
            </div>

            {/* Summary Section */}
            {summary && Object.keys(summary).length > 0 && (
              <div
                style={{
                  backgroundColor: "#1b3350",
                  padding: "20px",
                  borderRadius: "8px",
                  marginBottom: "20px",
                  border: "1px solid #333",
                  direction: dir,
                }}
              >
                <h3
                  style={{
                    color: "white",
                    marginBottom: "15px",
                    fontSize: "18px",
                    textAlign: headerAlign,
                  }}
                >
                  {t("projectReports.summary", {
                    defaultValue: "Report summary",
                  })}
                </h3>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "15px",
                    textAlign: headerAlign,
                  }}
                >
                  {summary.total_count !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.totalCount", {
                          defaultValue: "Total progress bills",
                        })}
                      </div>
                      <div
                        style={{
                          color: "white",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {summary.total_count || 0}
                      </div>
                    </div>
                  )}
                  {summary.total_value !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.totalValue", {
                          defaultValue: "Total value",
                        })}
                      </div>
                      <div
                        style={{
                          color: "#17a2b8",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {formatNumber(summary.total_value || 0)}
                      </div>
                    </div>
                  )}
                  {summary.total_net !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.totalNet", {
                          defaultValue: "Total net",
                        })}
                      </div>
                      <div
                        style={{
                          color: "#28a745",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {formatNumber(summary.total_net || 0)}
                      </div>
                    </div>
                  )}
                  {summary.total_retention !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.totalRetention", {
                          defaultValue: "Total retention",
                        })}
                      </div>
                      <div
                        style={{
                          color: "#f59f00",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {formatNumber(summary.total_retention || 0)}
                      </div>
                    </div>
                  )}
                  {summary.total_tax !== undefined && (
                    <div
                      style={{
                        backgroundColor: "#1a1f2e",
                        padding: "15px",
                        borderRadius: "6px",
                        border: "1px solid #333",
                        direction: dir,
                      }}
                    >
                      <div
                        style={{
                          color: "#9ca3af",
                          fontSize: "14px",
                          marginBottom: "5px",
                        }}
                      >
                        {t("projectReports.totalTax", {
                          defaultValue: "Total tax",
                        })}
                      </div>
                      <div
                        style={{
                          color: "#dc3545",
                          fontSize: "20px",
                          fontWeight: "bold",
                        }}
                      >
                        {formatNumber(summary.total_tax || 0)}
                      </div>
                    </div>
                  )}
                </div>

                {/* By Status Summary */}
                {summary.by_status &&
                  Object.keys(summary.by_status).length > 0 && (
                    <div style={{ marginTop: "20px" }}>
                      <h4
                        style={{
                          color: "white",
                          marginBottom: "10px",
                          fontSize: "16px",
                          textAlign: headerAlign,
                        }}
                      >
                        {t("projectReports.byStatus", {
                          defaultValue: "By status",
                        })}
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: "10px",
                          textAlign: headerAlign,
                        }}
                      >
                        {Object.entries(summary.by_status).map(
                          ([status, statusData]) => (
                            <div
                              key={status}
                              style={{
                                backgroundColor: "#1a1f2e",
                                padding: "12px",
                                borderRadius: "6px",
                                border: "1px solid #333",
                              }}
                            >
                              <div
                                style={{
                                  color: "#9ca3af",
                                  fontSize: "12px",
                                  marginBottom: "5px",
                                  textTransform: "capitalize",
                                }}
                              >
                                {translateStatus(status)}
                              </div>
                              <div
                                style={{
                                  color: "white",
                                  fontSize: "14px",
                                  marginBottom: "3px",
                                }}
                              >
                                {t("projectReports.count", {
                                  defaultValue: "Count",
                                })}
                                : <strong>{statusData.count || 0}</strong>
                              </div>
                              <div
                                style={{ color: "#17a2b8", fontSize: "14px" }}
                              >
                                {t("projectReports.total", {
                                  defaultValue: "Total",
                                })}
                                :{" "}
                                <strong>
                                  {formatNumber(statusData.total || 0)}
                                </strong>
                              </div>
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  )}
              </div>
            )}

            {progressBills && progressBills.length > 0 ? (
              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    backgroundColor: "#1b3350",
                  }}
                >
                  <thead>
                    <tr style={{ backgroundColor: "#AC2000" }}>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.billNo", {
                          defaultValue: "Bill No.",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.date", { defaultValue: "Date" })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.project", {
                          defaultValue: "Project",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.customer", {
                          defaultValue: "Customer",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.contract", {
                          defaultValue: "Contract",
                        })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.status", { defaultValue: "Status" })}
                      </th>
                      <th
                        style={{
                          padding: "12px",
                          textAlign: "center",
                          color: "white",
                          border: "1px solid #333",
                        }}
                      >
                        {t("projectReports.total", { defaultValue: "Total" })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {progressBills.map((bill, idx) => (
                      <tr
                        key={bill.id || idx}
                        style={{
                          backgroundColor:
                            idx % 2 === 0 ? "#1b3350" : "#1a1f2e",
                        }}
                      >
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {bill.bill_no || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {formatDate(bill.date)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {isRTL
                            ? bill.project?.name_ar ||
                              bill.project?.name_en ||
                              bill.project?.name
                            : bill.project?.name_en ||
                              bill.project?.name ||
                              bill.project?.name_ar ||
                              "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {getCustomerName(bill)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: isRTL ? "right" : "left",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {bill.contract?.code || "—"}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {translateStatus(bill.status)}
                        </td>
                        <td
                          style={{
                            padding: "12px",
                            textAlign: "center",
                            color: "white",
                            border: "1px solid #333",
                          }}
                        >
                          {formatNumber(bill.current_bill_amount || 0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                style={{
                  padding: "30px",
                  textAlign: "center",
                  color: "#9ca3af",
                }}
              >
                {t("projectReports.noData", { defaultValue: "No data" })}
              </div>
            )}
          </div>
        )}
      </div>

      {pdfType === "all" &&
        progressBillsReport &&
        pdfChunks &&
        pdfChunks.length > 0 &&
        pdfChunks.map((chunk, chunkIndex) => (
          <div
            key={chunkIndex}
            id={`progress-bills-report-pdf-all-${chunkIndex}`}
            aria-hidden="true"
            style={{
              position: "absolute",
              left: "-9999px",
              top: 0,
              width: "794px",
              height: "1123px",
              backgroundColor: "#ffffff",
              color: "#000000",
              fontFamily:
                '"Times New Roman Bold", "Times New Roman", "Times", serif',
              fontWeight: "bold",
              padding: "40px",
              boxSizing: "border-box",
              overflow: "visible",
            }}
          >
            <div
              style={{
                border: "5px solid #000",
                padding: "3px",
                backgroundColor: "#ffffff",
                boxSizing: "border-box",
                width: "100%",
                height: "1043px",
                overflow: "visible",
              }}
            >
              <div
                style={{
                  border: "2px solid #000",
                  minHeight: "1033px",
                  height: "1033px",
                  padding: "18px 18px 10px",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  fontFamily:
                    '"Times New Roman Bold", "Times New Roman", "Times", serif',
                  fontWeight: "bold",
                  WebkitFontSmoothing: "antialiased",
                  MozOsxFontSmoothing: "grayscale",
                  textRendering: "optimizeLegibility",
                  direction: dir,
                  backgroundColor: "#ffffff",
                  boxSizing: "border-box",
                  width: "100%",
                  overflow: "visible",
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    backgroundImage: `url(${headerLogo})`,
                    backgroundSize: "95%",
                    backgroundPosition: "center center",
                    backgroundRepeat: "no-repeat",
                    opacity: 0.08,
                    zIndex: 0,
                    pointerEvents: "none",
                  }}
                />
                <div
                  style={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                    minHeight: "100%",
                  }}
                >
                  <img
                    src={`${publicUrl}/HeaderAce.png`}
                    alt="Header"
                    style={{
                      width: "100%",
                      height: "auto",
                      objectFit: "contain",
                      marginBottom: "8px",
                      borderBottom: "4px double #000",
                      paddingBottom: "8px",
                      flexShrink: 0,
                    }}
                    crossOrigin="anonymous"
                  />

                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      overflow: "visible",
                      minHeight: 0,
                      paddingBottom: "120px",
                    }}
                  >
                    <div
                      style={{
                        marginTop: "12px",
                        textAlign: "center",
                        marginBottom: "20px",
                      }}
                    >
                      <h2
                        style={{
                          fontSize: "20px",
                          fontWeight: "700",
                          color: "#000080",
                          margin: 0,
                        }}
                      >
                        {t("projectReports.progressBills", {
                          defaultValue: "Progress Bills Reports",
                        })}
                      </h2>
                      <p
                        style={{
                          fontSize: "12px",
                          margin: "0 0 4px",
                          color: "#333",
                          fontFamily: '"Times New Roman", serif',
                          fontWeight: "normal",
                        }}
                      >
                        {new Date().toLocaleDateString(
                          isRTL ? "ar-SA" : "en-US",
                          { year: "numeric", month: "long", day: "numeric" },
                        )}
                      </p>
                      {(projectId ||
                        contractId ||
                        status ||
                        fromDate ||
                        toDate) && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#555",
                            direction: isRTL ? "rtl" : "ltr",
                            fontFamily: '"Times New Roman", serif',
                            fontWeight: "normal",
                          }}
                        >
                          {[
                            selectedProject &&
                              `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                            selectedContract &&
                              `${t("projectReports.contract", { defaultValue: "العقد" })}: ${getContractName(selectedContract)}`,
                            status &&
                              `${t("projectReports.status", { defaultValue: "الحالة" })}: ${translateStatus(status)}`,
                            fromDate &&
                              `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(fromDate)}`,
                            toDate &&
                              `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(toDate)}`,
                          ]
                            .filter(Boolean)
                            .join(" | ")}
                        </div>
                      )}
                    </div>
                    <div style={{ color: "#000", fontSize: "12px" }}>
                      {chunk && chunk.length > 0 ? (
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            border: "1px solid #000",
                            marginBottom: "15px",
                          }}
                        >
                          <thead>
                            <tr
                              style={{
                                backgroundColor: "rgba(238, 242, 247, 0.7)",
                                fontWeight: "bold",
                              }}
                            >
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                  width: "36px",
                                }}
                              >
                                #
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.billNo", {
                                  defaultValue: "Bill No.",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.date", {
                                  defaultValue: "Date",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.project", {
                                  defaultValue: "Project",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.customer", {
                                  defaultValue: "Customer",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.contract", {
                                  defaultValue: "Contract",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.status", {
                                  defaultValue: "Status",
                                })}
                              </th>
                              <th
                                style={{
                                  border: "1px solid #000",
                                  padding: "6px",
                                  textAlign: "center",
                                }}
                              >
                                {t("projectReports.total", {
                                  defaultValue: "Total",
                                })}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {chunk.map((bill, idx) => (
                              <tr
                                key={bill.id || idx}
                                style={{
                                  backgroundColor: "rgba(255, 255, 255, 0.7)",
                                }}
                              >
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                    width: "36px",
                                  }}
                                >
                                  {chunkIndex * 20 + idx + 1}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                  }}
                                >
                                  {bill.bill_no || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                  }}
                                >
                                  {formatDate(bill.date)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: isRTL ? "right" : "left",
                                  }}
                                >
                                  {isRTL
                                    ? bill.project?.name_ar ||
                                      bill.project?.name_en ||
                                      bill.project?.name
                                    : bill.project?.name_en ||
                                      bill.project?.name ||
                                      bill.project?.name_ar ||
                                      "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: isRTL ? "right" : "left",
                                  }}
                                >
                                  {getCustomerName(bill)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: isRTL ? "right" : "left",
                                  }}
                                >
                                  {bill.contract?.code || "—"}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                  }}
                                >
                                  {translateStatus(bill.status)}
                                </td>
                                <td
                                  style={{
                                    border: "1px solid #000",
                                    padding: "6px",
                                    textAlign: "center",
                                  }}
                                >
                                  {formatNumber(bill.current_bill_amount || 0)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div style={{ textAlign: "center", padding: "20px" }}>
                          {t("projectReports.noData", {
                            defaultValue: "No data",
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  <div
                    style={{
                      position: "absolute",
                      bottom: "10px",
                      left: "18px",
                      right: "18px",
                      flexShrink: 0,
                    }}
                  >
                    <img
                      src={`${publicUrl}/FooterAce.png`}
                      alt="Footer"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                          borderTop: "4px double #000",
                          paddingTop: "8px",
                      }}
                      crossOrigin="anonymous"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}

      {isPreviewOpen &&
        progressBillsReport &&
        createPortal(
          <div
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0, 0, 0, 0.9)",
              zIndex: 999999,
              display: "flex",
              flexDirection: "column",
              overflow: "auto",
              padding: "20px",
              boxSizing: "border-box",
            }}
            onClick={e => {
              if (e.target === e.currentTarget) {
                setIsPreviewOpen(false);
              }
            }}
          >
            <div
              style={{
                position: "fixed",
                top: "20px",
                right: "20px",
                display: "flex",
                gap: "10px",
                zIndex: 1000000,
              }}
            >
              <button
                onClick={() => setPreviewZoom(prev => Math.min(2, prev + 0.1))}
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                +
              </button>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: "50px",
                  padding: "8px 12px",
                  backgroundColor: "rgba(0, 0, 0, 0.5)",
                  borderRadius: "6px",
                  color: "white",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                {Math.round(previewZoom * 100)}%
              </div>
              <button
                onClick={() =>
                  setPreviewZoom(prev => Math.max(0.1, prev - 0.1))
                }
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                −
              </button>
              <button
                onClick={() => {
                  setPreviewZoom(1.0);
                  setPreviewPosition({ x: 0, y: 0 });
                }}
                style={{
                  backgroundColor: "#333",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                {t("projectReports.preview.fitToScreen", {
                  defaultValue: "ملاءمة الشاشة",
                })}
              </button>
              <button
                onClick={handlePrintAll}
                disabled={isGeneratingPdf}
                style={{
                  backgroundColor: isGeneratingPdf ? "#666" : "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: isGeneratingPdf ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  opacity: isGeneratingPdf ? 0.6 : 1,
                }}
              >
                <FaFilePdf />{" "}
                {t("projectReports.printPDF", { defaultValue: "Print PDF" })}
              </button>
              <button
                onClick={() => setIsPreviewOpen(false)}
                style={{
                  backgroundColor: "#dc3545",
                  color: "white",
                  border: "none",
                  padding: "10px 15px",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontSize: "14px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <FaTimes /> {t("btns.close", { defaultValue: "إغلاق" })}
              </button>
            </div>

            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "center",
                alignItems: "flex-start",
                padding: "80px 20px 20px",
                minHeight: "calc(100vh - 40px)",
                boxSizing: "border-box",
              }}
            >
              <div
                style={{
                  transform:
                    previewZoom > 1
                      ? `translate(${previewPosition.x}px, ${previewPosition.y}px) scale(${previewZoom})`
                      : `scale(${previewZoom})`,
                  transformOrigin:
                    previewZoom > 1 ? "center center" : "top center",
                  width: "794px",
                  height: "auto",
                  cursor:
                    previewZoom > 1
                      ? isDragging
                        ? "grabbing"
                        : "grab"
                      : "default",
                  transition: isDragging ? "none" : "transform 0.1s",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.5)",
                  margin: "0 auto",
                }}
                onMouseDown={e => {
                  if (e.button === 0 && previewZoom > 1) {
                    setIsDragging(true);
                    setDragStart({
                      x: e.clientX - previewPosition.x,
                      y: e.clientY - previewPosition.y,
                    });
                  }
                }}
              >
                {pdfChunks && pdfChunks.length > 0 ? (
                  pdfChunks.map((chunk, chunkIndex) => (
                    <div
                      key={chunkIndex}
                      id={`preview-progress-bills-report-pdf-all-${chunkIndex}`}
                      style={{
                        marginBottom:
                          chunkIndex < pdfChunks.length - 1 ? "20px" : "0",
                      }}
                    >
                      <div
                        style={{
                          border: "5px solid #000",
                          padding: "3px",
                          backgroundColor: "#ffffff",
                          boxSizing: "border-box",
                          width: "794px",
                          minHeight: "1043px",
                          height: "auto",
                          overflow: "visible",
                        }}
                      >
                        <div
                          style={{
                            border: "2px solid #000",
                            minHeight: "1033px",
                            height: "1033px",
                            padding: "18px 18px 10px",
                            position: "relative",
                            display: "flex",
                            flexDirection: "column",
                            fontFamily:
                              '"Times New Roman Bold", "Times New Roman", "Times", serif',
                            fontWeight: "bold",
                            WebkitFontSmoothing: "antialiased",
                            MozOsxFontSmoothing: "grayscale",
                            textRendering: "optimizeLegibility",
                            direction: dir,
                            backgroundColor: "#ffffff",
                            boxSizing: "border-box",
                            width: "100%",
                            overflow: "visible",
                          }}
                        >
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "100%",
                              backgroundImage: `url(${headerLogo})`,
                              backgroundSize: "95%",
                              backgroundPosition: "center center",
                              backgroundRepeat: "no-repeat",
                              opacity: 0.08,
                              zIndex: 0,
                              pointerEvents: "none",
                            }}
                          />
                          <div
                            style={{
                              position: "relative",
                              zIndex: 1,
                              display: "flex",
                              flexDirection: "column",
                              height: "100%",
                              minHeight: "100%",
                            }}
                          >
                            <img
                              src={`${publicUrl}/HeaderAce.png`}
                              alt="Header"
                              style={{
                                width: "100%",
                                height: "auto",
                                objectFit: "contain",
                                marginBottom: "8px",
                                borderBottom: "4px double #000",
                                paddingBottom: "8px",
                                flexShrink: 0,
                              }}
                              crossOrigin="anonymous"
                            />

                            <div
                              style={{
                                flex: 1,
                                display: "flex",
                                flexDirection: "column",
                                overflow: "visible",
                                minHeight: 0,
                                paddingBottom: "120px",
                              }}
                            >
                              <div
                                style={{
                                  marginTop: "12px",
                                  textAlign: "center",
                                  marginBottom: "20px",
                                }}
                              >
                                <h2
                                  style={{
                                    fontSize: "20px",
                                    fontWeight: "700",
                                    color: "#000080",
                                    margin: 0,
                                  }}
                                >
                                  {t("projectReports.progressBills", {
                                    defaultValue: "Progress Bills Reports",
                                  })}
                                </h2>
                                <p
                                  style={{
                                    fontSize: "12px",
                                    margin: "0 0 4px",
                                    color: "#333",
                                    fontFamily: '"Times New Roman", serif',
                                    fontWeight: "normal",
                                  }}
                                >
                                  {new Date().toLocaleDateString(
                                    isRTL ? "ar-SA" : "en-US",
                                    {
                                      year: "numeric",
                                      month: "long",
                                      day: "numeric",
                                    },
                                  )}
                                </p>
                                {(projectId ||
                                  contractId ||
                                  status ||
                                  fromDate ||
                                  toDate) && (
                                  <div
                                    style={{
                                      fontSize: "11px",
                                      color: "#555",
                                      direction: isRTL ? "rtl" : "ltr",
                                      fontFamily: '"Times New Roman", serif',
                                      fontWeight: "normal",
                                    }}
                                  >
                                    {[
                                      selectedProject &&
                                        `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                                      selectedContract &&
                                        `${t("projectReports.contract", { defaultValue: "العقد" })}: ${getContractName(selectedContract)}`,
                                      status &&
                                        `${t("projectReports.status", { defaultValue: "الحالة" })}: ${translateStatus(status)}`,
                                      fromDate &&
                                        `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(fromDate)}`,
                                      toDate &&
                                        `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(toDate)}`,
                                    ]
                                      .filter(Boolean)
                                      .join(" | ")}
                                  </div>
                                )}
                              </div>
                              <div style={{ color: "#000", fontSize: "12px" }}>
                                {chunk && chunk.length > 0 ? (
                                  <table
                                    style={{
                                      width: "100%",
                                      borderCollapse: "collapse",
                                      border: "1px solid #000",
                                      marginBottom: "15px",
                                    }}
                                  >
                                    <thead>
                                      <tr
                                        style={{
                                          backgroundColor:
                                            "rgba(238, 242, 247, 0.7)",
                                          fontWeight: "bold",
                                        }}
                                      >
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                            width: "36px",
                                          }}
                                        >
                                          #
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.billNo", {
                                            defaultValue: "Bill No.",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.date", {
                                            defaultValue: "Date",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.project", {
                                            defaultValue: "Project",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.customer", {
                                            defaultValue: "Customer",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.contract", {
                                            defaultValue: "Contract",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.status", {
                                            defaultValue: "Status",
                                          })}
                                        </th>
                                        <th
                                          style={{
                                            border: "1px solid #000",
                                            padding: "6px",
                                            textAlign: "center",
                                          }}
                                        >
                                          {t("projectReports.total", {
                                            defaultValue: "Total",
                                          })}
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {chunk.map((bill, idx) => (
                                        <tr
                                          key={bill.id || idx}
                                          style={{
                                            backgroundColor:
                                              "rgba(255, 255, 255, 0.7)",
                                          }}
                                        >
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: "center",
                                              width: "36px",
                                            }}
                                          >
                                            {chunkIndex * 20 + idx + 1}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: "center",
                                            }}
                                          >
                                            {bill.bill_no || "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: "center",
                                            }}
                                          >
                                            {formatDate(bill.date)}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                            }}
                                          >
                                            {isRTL
                                              ? bill.project?.name_ar ||
                                                bill.project?.name_en ||
                                                bill.project?.name
                                              : bill.project?.name_en ||
                                                bill.project?.name ||
                                                bill.project?.name_ar ||
                                                "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                            }}
                                          >
                                            {getCustomerName(bill)}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: isRTL
                                                ? "right"
                                                : "left",
                                            }}
                                          >
                                            {bill.contract?.code || "—"}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: "center",
                                            }}
                                          >
                                            {translateStatus(bill.status)}
                                          </td>
                                          <td
                                            style={{
                                              border: "1px solid #000",
                                              padding: "6px",
                                              textAlign: "center",
                                            }}
                                          >
                                            {formatNumber(
                                              bill.current_bill_amount || 0,
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div
                                    style={{
                                      textAlign: "center",
                                      padding: "20px",
                                    }}
                                  >
                                    {t("projectReports.noData", {
                                      defaultValue: "No data",
                                    })}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div
                              style={{
                                position: "absolute",
                                bottom: "10px",
                                left: "18px",
                                right: "18px",
                                flexShrink: 0,
                              }}
                            >
                              <img
                                src={`${publicUrl}/FooterAce.png`}
                                alt="Footer"
                                style={{
                                  width: "100%",
                                  height: "auto",
                                  objectFit: "contain",
                                    borderTop: "4px double #000",
                                    paddingTop: "8px",
                                }}
                                crossOrigin="anonymous"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div
                    id="preview-progress-bills-report-pdf-all"
                    style={{
                      border: "5px solid #000",
                      padding: "3px",
                      backgroundColor: "#ffffff",
                      boxSizing: "border-box",
                      width: "794px",
                      minHeight: "1043px",
                      height: "auto",
                      overflow: "visible",
                    }}
                  >
                    <div
                      style={{
                        border: "2px solid #000",
                        minHeight: "1033px",
                        height: "1033px",
                        padding: "18px 18px 10px",
                        position: "relative",
                        display: "flex",
                        flexDirection: "column",
                        fontFamily:
                          '"Times New Roman Bold", "Times New Roman", "Times", serif',
                        fontWeight: "bold",
                        WebkitFontSmoothing: "antialiased",
                        MozOsxFontSmoothing: "grayscale",
                        textRendering: "optimizeLegibility",
                        direction: dir,
                        backgroundColor: "#ffffff",
                        boxSizing: "border-box",
                        width: "100%",
                        overflow: "visible",
                      }}
                    >
                      <div
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          width: "100%",
                          height: "100%",
                          backgroundImage: `url(${headerLogo})`,
                          backgroundSize: "95%",
                          backgroundPosition: "center center",
                          backgroundRepeat: "no-repeat",
                          opacity: 0.08,
                          zIndex: 0,
                          pointerEvents: "none",
                        }}
                      />
                      <div
                        style={{
                          position: "relative",
                          zIndex: 1,
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          minHeight: "100%",
                        }}
                      >
                        <img
                          src={`${publicUrl}/HeaderAce.png`}
                          alt="Header"
                          style={{
                            width: "100%",
                            height: "auto",
                            objectFit: "contain",
                            marginBottom: "8px",
                            borderBottom: "4px double #000",
                            paddingBottom: "8px",
                            flexShrink: 0,
                          }}
                          crossOrigin="anonymous"
                        />

                        <div
                          style={{
                            flex: 1,
                            display: "flex",
                            flexDirection: "column",
                            overflow: "visible",
                            minHeight: 0,
                            paddingBottom: "120px",
                          }}
                        >
                          <div
                            style={{
                              marginTop: "12px",
                              textAlign: "center",
                              marginBottom: "20px",
                            }}
                          >
                            <h2
                              style={{
                                fontSize: "20px",
                                fontWeight: "700",
                                color: "#000080",
                                margin: 0,
                              }}
                            >
                              {t("projectReports.progressBills", {
                                defaultValue: "Progress Bills Reports",
                              })}
                            </h2>
                            <p
                              style={{
                                fontSize: "12px",
                                margin: "0 0 4px",
                                color: "#333",
                                fontFamily: '"Times New Roman", serif',
                                fontWeight: "normal",
                              }}
                            >
                              {new Date().toLocaleDateString(
                                isRTL ? "ar-SA" : "en-US",
                                {
                                  year: "numeric",
                                  month: "long",
                                  day: "numeric",
                                },
                              )}
                            </p>
                            {(projectId ||
                              contractId ||
                              status ||
                              fromDate ||
                              toDate) && (
                              <div
                                style={{
                                  fontSize: "11px",
                                  color: "#555",
                                  direction: isRTL ? "rtl" : "ltr",
                                  fontFamily: '"Times New Roman", serif',
                                  fontWeight: "normal",
                                }}
                              >
                                {[
                                  selectedProject &&
                                    `${t("projectReports.project", { defaultValue: "المشروع" })}: ${getProjectName(selectedProject)}`,
                                  selectedContract &&
                                    `${t("projectReports.contract", { defaultValue: "العقد" })}: ${getContractName(selectedContract)}`,
                                  status &&
                                    `${t("projectReports.status", { defaultValue: "الحالة" })}: ${translateStatus(status)}`,
                                  fromDate &&
                                    `${t("projectReports.fromDate", { defaultValue: "من" })}: ${formatDate(fromDate)}`,
                                  toDate &&
                                    `${t("projectReports.toDate", { defaultValue: "إلى" })}: ${formatDate(toDate)}`,
                                ]
                                  .filter(Boolean)
                                  .join(" | ")}
                              </div>
                            )}
                          </div>
                          <div style={{ color: "#000", fontSize: "12px" }}>
                            {progressBills && progressBills.length > 0 ? (
                              <table
                                style={{
                                  width: "100%",
                                  borderCollapse: "collapse",
                                  border: "1px solid #000",
                                  marginBottom: "15px",
                                }}
                              >
                                <thead>
                                  <tr
                                    style={{
                                      backgroundColor:
                                        "rgba(238, 242, 247, 0.7)",
                                      fontWeight: "bold",
                                    }}
                                  >
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                        width: "36px",
                                      }}
                                    >
                                      #
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.billNo", {
                                        defaultValue: "Bill No.",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.date", {
                                        defaultValue: "Date",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.project", {
                                        defaultValue: "Project",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.customer", {
                                        defaultValue: "Customer",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.contract", {
                                        defaultValue: "Contract",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.status", {
                                        defaultValue: "Status",
                                      })}
                                    </th>
                                    <th
                                      style={{
                                        border: "1px solid #000",
                                        padding: "6px",
                                        textAlign: "center",
                                      }}
                                    >
                                      {t("projectReports.total", {
                                        defaultValue: "Total",
                                      })}
                                    </th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {progressBills.map((bill, idx) => (
                                    <tr
                                      key={bill.id || idx}
                                      style={{
                                        backgroundColor:
                                          "rgba(255, 255, 255, 0.7)",
                                      }}
                                    >
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: "center",
                                          width: "36px",
                                        }}
                                      >
                                        {idx + 1}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: "center",
                                        }}
                                      >
                                        {bill.bill_no || "—"}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: "center",
                                        }}
                                      >
                                        {formatDate(bill.date)}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: isRTL ? "right" : "left",
                                        }}
                                      >
                                        {isRTL
                                          ? bill.project?.name_ar ||
                                            bill.project?.name_en ||
                                            bill.project?.name
                                          : bill.project?.name_en ||
                                            bill.project?.name ||
                                            bill.project?.name_ar ||
                                            "—"}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: isRTL ? "right" : "left",
                                        }}
                                      >
                                        {getCustomerName(bill)}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: isRTL ? "right" : "left",
                                        }}
                                      >
                                        {bill.contract?.code || "—"}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: "center",
                                        }}
                                      >
                                        {translateStatus(bill.status)}
                                      </td>
                                      <td
                                        style={{
                                          border: "1px solid #000",
                                          padding: "6px",
                                          textAlign: "center",
                                        }}
                                      >
                                        {formatNumber(bill.current_bill_amount || 0)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            ) : (
                              <div
                                style={{ textAlign: "center", padding: "20px" }}
                              >
                                {t("projectReports.noData", {
                                  defaultValue: "No data",
                                })}
                              </div>
                            )}
                          </div>
                        </div>

                        <div
                          style={{
                            position: "absolute",
                            bottom: "10px",
                            left: "18px",
                            right: "18px",
                            flexShrink: 0,
                          }}
                        >
                          <img
                            src={`${publicUrl}/FooterAce.png`}
                            alt="Footer"
                            style={{
                              width: "100%",
                              height: "auto",
                              objectFit: "contain",
                                borderTop: "4px double #000",
                                paddingTop: "8px",
                            }}
                            crossOrigin="anonymous"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
};

export default ProgressBillsReport;
